import { localize, readAppLanguage } from '../i18n';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import { journey } from '../utils/productJourneys';
import Dialog from '../components/StableDialog';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, DialogActions, DialogContent, DialogContentText, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PageHeader from '../components/PageHeader';
import BottomSheet from '../components/BottomSheet';
import AppToast from '../components/AppToast';
import EditTakenTimeSheet from '../components/EditTakenTimeSheet';
import CalendarGrid from '../components/calendar/CalendarGrid';
import DayDetailCard from '../components/calendar/DayDetailCard';
import { colorOf, colorOfTime } from '../utils/medColor';
import { getConfirmationWindow, getDeviceUuid, getGuardianTargets, getGuardianTargetCalendar, getCalendarView, deleteLog, confirmLog, updateConfirmedAt } from '../api';
import useRequestGuard from '../hooks/useRequestGuard';
import { colors } from '../design/tokens';
import { clearMedicationCaches } from '../utils/localCache';

function pad2(n) {
    return String(n).padStart(2, '0');
}

function aggregateStatus(logs) {
    // 시간 그룹 안의 같은 약은 unique 제약상 1 로그뿐이라 단일 status를 매핑하면 충분.
    if (logs.length === 0) return null;
    if (logs[0].frequencyType === 'AS_NEEDED') return 'asNeeded';
    switch (logs[0].status) {
        case 'TAKEN': return 'taken';
        case 'MISSED': return 'missed';
        case 'SKIPPED': return 'skipped';
        default: return 'pending';
    }
}

function CalendarTargetStrip({ children }) {
    const scroller = React.useRef(null);
    const [position, setPosition] = useState({ width: 1, left: 0 });
    const measure = React.useCallback(() => {
        const el = scroller.current;
        if (!el) return;
        const width = Math.min(1, el.clientWidth / Math.max(1, el.scrollWidth));
        const left = Math.max(0, Math.min(1 - width, el.scrollLeft / Math.max(1, el.scrollWidth)));
        setPosition(previous => previous.width === width && previous.left === left ? previous : { width, left });
    }, []);
    useLayoutEffect(() => {
        const el = scroller.current;
        measure();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        Array.from(el.children).forEach(child => observer.observe(child));
        return () => observer.disconnect();
    }, [children, measure]);
    return <Box sx={{ position: 'relative' }}>
        <Box ref={scroller} onScroll={measure} role="group" aria-label={localize("캘린더 선택")} sx={{
            display: 'flex', flexWrap: 'nowrap', gap: 1,
            overflowX: 'auto', overscrollBehaviorX: 'contain',
            WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
            p: '4px', m: '-4px', '&::-webkit-scrollbar': { display: 'none' },
        }}>{children}</Box>
        {position.width < 1 && <Box aria-hidden="true" data-calendar-scroll-track sx={{
            position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 64, height: 2,
            bgcolor: '#e5e7eb', borderRadius: 2, pointerEvents: 'none',
        }}>
            <Box sx={{ position: 'absolute', height: '100%', borderRadius: 2, bgcolor: '#aeb4bc',
                width: `${position.width * 100}%`, left: `${position.left * 100}%` }} />
        </Box>}
    </Box>;
}

export default function CalendarPage() {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [targets, setTargets] = useState([]);
    const [targetId, setTargetId] = useState(null);
    const [targetsError, setTargetsError] = useState(false);
    const [refreshVersion, setRefreshVersion] = useState(0);
    const targetsInFlight = React.useRef(null);
    const journeyLoaded = React.useRef(false);
    const recordJourneyLoaded = React.useCallback(() => {
        if (!journeyLoaded.current) { journeyLoaded.current = true; journey('calendar', 'complete'); }
    }, []);
    const beginRequest = useRequestGuard('calendar');
    const loadTargets = React.useCallback(async (refreshCalendar = false) => {
        if (targetsInFlight.current?.()) return;
        const isCurrent = beginRequest('targets');
        targetsInFlight.current = isCurrent;
        try {
            const data = await getGuardianTargets(getDeviceUuid());
            if (!isCurrent()) return;
            setTargets(data);
            setTargetsError(false);
            if (refreshCalendar === true) setRefreshVersion(value => value + 1);
            setTargetId(id => data.some(t => t.groupId === id) ? id : null);
        } catch {
            if (!isCurrent()) return;
            setTargetsError(true);
            setTargets([]);
            setTargetId(null);
        } finally {
            if (targetsInFlight.current === isCurrent) targetsInFlight.current = null;
        }
    }, [beginRequest]);
    useEffect(() => {
        loadTargets();
        const refresh = () => { if (document.visibilityState !== 'hidden') loadTargets(true); };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [loadTargets]);
    const target = targets.find(t => t.groupId === targetId);
    const nameOf = (t, index) => t.patientNickname?.trim() || localize("가족 {v0}", { v0: index + 1 });
    const ownerName = target ? nameOf(target, targets.indexOf(target)) : localize('내');
    return (
        <Box sx={{ pb: 2, bgcolor: '#f5f5f5' }}>
            {/* 뒤로가기 헤더의 실제 높이와 공통 spacer 차이(16px)를 이 화면에서 보정한다. */}
            <Box sx={{ pb: 2 }}><PageHeader back title={localize("복용 기록")} /></Box>
            {targets.length > 0 && (
                <Box sx={{ px: 2, pt: 2.5 }}>
                    <SingleLineText sx={{ fontSize: 14, fontWeight: 400, color: colors.textSecondary, mb: 0.25, ml: 0.5 }}>{localize("보고 싶은 사람을 선택해 주세요")}</SingleLineText>
                    <CalendarTargetStrip>
                        {[{ groupId: null }, ...targets].map((t, index) => (
                            <Button key={t.groupId ?? 'self'} aria-pressed={targetId === t.groupId}
                                onClick={() => setTargetId(t.groupId)}
                                disableRipple
                                sx={{ flexShrink: 0, minHeight: 48, minWidth: 48, maxWidth: '100%', px: 1.5, py: 0,
                                    position: 'relative', borderRadius: '10px', isolation: 'isolate',
                                    fontSize: 14, lineHeight: 1.4, transition: 'none',
                                    bgcolor: 'transparent', color: targetId === t.groupId ? colors.text : colors.textSecondary,
                                    fontWeight: targetId === t.groupId ? 700 : 500,
                                    '&::before': {
                                        content: '""', position: 'absolute', inset: '7px 0', zIndex: -1,
                                        borderRadius: '10px', bgcolor: colors.surface,
                                        border: '1px solid', borderColor: targetId === t.groupId ? colors.brand : '#e5e7eb',
                                        boxShadow: targetId === t.groupId ? `inset 0 0 0 1px ${colors.brand}` : 'none',
                                    },
                                    '&:hover, &:active': { bgcolor: 'transparent' },
                                    '&.Mui-focusVisible': { outline: 'none' },
                                    '&.Mui-focusVisible::before': { outline: `2px solid ${colors.text}`, outlineOffset: 2 },
                                }}>
                                {index === 0 ? localize("내 캘린더") : nameOf(t, index - 1)}
                            </Button>
                        ))}
                    </CalendarTargetStrip>
                </Box>
            )}
            {targetsError && <Box sx={{ px: 2, pt: 2 }}>
                <SingleLineText sx={{ fontSize: 12, color: '#777' }}>{localize("가족 목록을 불러오지 못했어요")}</SingleLineText>
                <Button onClick={() => loadTargets(true)}>{localize("가족 목록 다시 불러오기")}</Button>
            </Box>}
            <CalendarContent key={`${targetId ?? 'self'}:${target?.sharingStatus || 'ACTIVE'}:${year}-${month}`}
                target={target} ownerName={ownerName} showOwner={targets.length > 0}
                refreshVersion={refreshVersion} recordJourneyLoaded={recordJourneyLoaded}
                year={year} month={month} setYear={setYear} setMonth={setMonth} />
        </Box>
    );
}

function CalendarContent({ target, ownerName, showOwner, year, month, setYear, setMonth, refreshVersion, recordJourneyLoaded }) {
    const today = new Date();
    const readOnly = !!target;
    const sharingPaused = target && target.sharingStatus && target.sharingStatus !== 'ACTIVE';
    const groupId = target?.groupId;
    const beginRequest = useRequestGuard('calendar');
    const [error, setError] = useState('');
    const [calendarData, setCalendarData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletingIds, setDeletingIds] = useState(() => new Set());
    const [processingIds, setProcessingIds] = useState(() => new Set());
    const [timeSheet, setTimeSheet] = useState({
        open: false,
        mode: 'confirm',
        log: null,
        initialTime: '',
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const calendarRequestRef = React.useRef(0);
    const monthStr = `${year}-${pad2(month)}`;

    const loadCalendar = React.useCallback(() => {
        const requestId = ++calendarRequestRef.current;
        const isCurrent = beginRequest();
        if (!isCurrent()) return Promise.resolve();
        setCalendarData(null);
        setError('');
        if (sharingPaused) { setLoading(false); return Promise.resolve(); }
        setLoading(true);
        const uuid = getDeviceUuid();
        return (groupId != null ? getGuardianTargetCalendar(uuid, groupId, monthStr) : getCalendarView(uuid, monthStr))
            .then(data => { if (isCurrent() && requestId === calendarRequestRef.current) { setCalendarData(data); recordJourneyLoaded(); } })
            .catch((err) => {
                if (!isCurrent() || requestId !== calendarRequestRef.current) return;
                console.error(err);
                setSelectedDate(null);
                setError(err?.response?.status === 403 ? localize("가족 연결 또는 공유 상태를 확인해주세요") : localize("복용 기록을 불러오지 못했어요"));
            })
            .finally(() => { if (isCurrent() && requestId === calendarRequestRef.current) setLoading(false); });
    }, [monthStr, groupId, sharingPaused, beginRequest, recordJourneyLoaded]);

    useEffect(() => {
        loadCalendar();
        return () => { calendarRequestRef.current++; };
    }, [loadCalendar, refreshVersion]);

    const prevMonth = () => {
        const ny = month === 1 ? year - 1 : year;
        const nm = month === 1 ? 12 : month - 1;
        setYear(ny);
        setMonth(nm);
        setSelectedDate(null);
    };
    const nextMonth = () => {
        const ny = month === 12 ? year + 1 : year;
        const nm = month === 12 ? 1 : month + 1;
        setYear(ny);
        setMonth(nm);
        setSelectedDate(null);
    };

    // 같은 시각/같은 날의 약들을 status별로 그룹 칩으로 머지
    const cellMedsByDay = useMemo(() => {
        const map = new Map();
        if (!calendarData?.logs) return map;
        // dateStr → time(HH:MM) → medicationId → logs[]
        const nested = new Map();
        for (const log of calendarData.logs) {
            const ds = log.scheduledDate;
            const tm = log.scheduledTime.slice(0, 5);
            const medId = log.medicationId;
            if (!nested.has(ds)) nested.set(ds, new Map());
            const byTime = nested.get(ds);
            if (!byTime.has(tm)) byTime.set(tm, new Map());
            const byMed = byTime.get(tm);
            if (!byMed.has(medId)) byMed.set(medId, []);
            byMed.get(medId).push(log);
        }
        for (const [ds, byTime] of nested.entries()) {
            const chips = [];
            const sortedTimes = Array.from(byTime.keys()).sort();
            for (const tm of sortedTimes) {
                const byMed = byTime.get(tm);
                const [hh, mm] = tm.split(':');
                const hour = parseInt(hh, 10);
                const timeLabel = `${String(hour).padStart(2, '0')}:${mm}`;
                // 약별 status 종합 후 status별로 분리
                const byStatus = {};
                for (const [medId, medLogs] of byMed.entries()) {
                    const s = aggregateStatus(medLogs);
                    if (!s) continue;
                    if (!byStatus[s]) byStatus[s] = [];
                    byStatus[s].push({ medId, name: medLogs[0].medicationName });
                }
                // status별 칩 생성 (1개면 약 이름, 2개+면 시간만). PRN은 표시만 하고 완료율 계산에는 영향 없음.
                for (const [s, meds] of Object.entries(byStatus)) {
                    if (meds.length === 1) {
                        chips.push({
                            key: `${tm}-${s}-${meds[0].medId}`,
                            name: meds[0].name,
                            color: colorOf(meds[0].medId),
                            status: s,
                        });
                    } else {
                        chips.push({
                            key: `${tm}-${s}-multi`,
                            name: timeLabel,
                            isGroup: true,
                            color: colorOfTime(hour),
                            status: s,
                        });
                    }
                }
            }
            map.set(ds, chips);
        }
        return map;
    }, [calendarData]);

    const selectedLogs = useMemo(() => {
        if (!calendarData?.logs || !selectedDate) return [];
        return calendarData.logs.filter((l) => l.scheduledDate === selectedDate);
    }, [calendarData, selectedDate]);

    const selectedDayInfo = useMemo(() => {
        if (!calendarData?.days || !selectedDate) return null;
        return calendarData.days.find((d) => d.date === selectedDate) || null;
    }, [calendarData, selectedDate]);

    const handleDeleteConfirm = async () => {
        if (readOnly || !deleteTarget || deletingIds.has(deleteTarget.id)) return;
        const target = deleteTarget;
        setDeletingIds((prev) => new Set(prev).add(target.id));
        try {
            const uuid = getDeviceUuid();
            await deleteLog(uuid, target.id);
            setDeleteTarget(null);
            clearMedicationCaches();
            await loadCalendar();
            setSnackbar({ open: true, message: localize("복용 기록을 삭제했어요"), severity: 'success' });
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: localize("기록 삭제에 실패했어요"), severity: 'error' });
        } finally {
            setDeletingIds((prev) => {
                const next = new Set(prev);
                next.delete(target.id);
                return next;
            });
        }
    };

    const openMissedConfirmSheet = (log) => {
        setTimeSheet({
            open: true,
            mode: 'confirm',
            log,
            initialTime: log.scheduledTime?.slice(0, 5) || '12:00',
        });
    };

    const openEditTimeSheet = (log) => {
        const confirmedTime = log.confirmedAt?.slice(11, 16);
        setTimeSheet({
            open: true,
            mode: 'edit',
            log,
            initialTime: confirmedTime || log.scheduledTime?.slice(0, 5) || '12:00',
        });
    };

    const handleTimeSheetConfirm = async (newTime, takenDate) => {
        const log = timeSheet.log;
        if (readOnly || !log) return;
        const confirmedAt = `${takenDate || log.scheduledDate}T${newTime}:00`;
        setProcessingIds((prev) => new Set(prev).add(log.id));
        try {
            const uuid = getDeviceUuid();
            if (timeSheet.mode === 'confirm') {
                await confirmLog(uuid, log.id, confirmedAt);
                setSnackbar({ open: true, message: localize('복용 기록을 남겼어요'), severity: 'success' });
            } else {
                await updateConfirmedAt(uuid, log.id, confirmedAt);
                setSnackbar({ open: true, message: localize("복용 시각을 변경했어요"), severity: 'success' });
            }
            clearMedicationCaches();
            await loadCalendar();
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || (timeSheet.mode === 'confirm' ? localize('기록을 남기지 못했어요') : localize('기록을 바꾸지 못했어요')),
                severity: 'error',
            });
            throw err;
        } finally {
            setProcessingIds((prev) => {
                const next = new Set(prev);
                next.delete(log.id);
                return next;
            });
        }
    };

    // 월간 요약
    const monthStats = useMemo(() => {
        if (!calendarData?.days) return null;
        const todayKey = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
        const daysWithLogs = calendarData.days.filter((d) => d.date <= todayKey && d.status !== 'NONE');
        if (daysWithLogs.length === 0) return null;
        const allTaken = daysWithLogs.filter((d) => d.status === 'ALL_TAKEN').length;
        const rate = Math.round((allTaken / daysWithLogs.length) * 100);
        return { total: daysWithLogs.length, allTaken, rate };
    }, [calendarData]);

    const adherenceMonth = readAppLanguage() === 'en' ? new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 1))) : month;

    return (
        <Box>
            <Box sx={{ px: 2, pt: showOwner ? 1 : 2.5, '& > *:last-child': { mb: 0 } }}>
                {/* 월간 요약 카드 — 페이지 최상단 */}
                {monthStats && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            mb: 1.5,
                            bgcolor: '#fff',
                            border: '1px solid #eee',
                            borderRadius: '16px',
                        }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1, pr: 2 }}>
                            <Typography sx={{ fontSize: 14, color: colors.textSecondary, fontWeight: 600, overflowWrap: 'anywhere' }}>
                                {showOwner ? localize("{v0} {v1}월 복용률", { v0: readOnly ? localize("{v0}의", { v0: ownerName }) : localize('내'), v1: adherenceMonth }) : localize("{v0}월 복용률", { v0: adherenceMonth })}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '22px',
                                    fontWeight: 800,
                                    color:
                                        monthStats.rate >= 80
                                            ? '#15803d'
                                            : monthStats.rate >= 50
                                                ? '#d97706'
                                                : '#dc2626',
                                }}
                            >
                                {monthStats.rate}%
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#22c55e' }}>
                                    {monthStats.allTaken}
                                </Typography>
                                <SingleLineText sx={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>{localize("완료")}</SingleLineText>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#ef4444' }}>
                                    {monthStats.total - monthStats.allTaken}
                                </Typography>
                                <SingleLineText sx={{ fontSize: '11px', color: '#999', fontWeight: 600 }}>{localize("미완료")}</SingleLineText>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* 캘린더 카드 */}
                <Box
                    sx={{
                        bgcolor: '#fff',
                        border: '1px solid #eee',
                        borderRadius: '16px',
                        padding: '10px 3px 6px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 1,
                            mb: '6px',
                        }}
                    >
                        <Box
                            component="button"
                            aria-label={localize("이전 달")}
                            onClick={prevMonth}
                            sx={{
                                p: '6px', minWidth: 44, minHeight: 44, border: 0, bgcolor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                                color: '#777',
                                '&:active': { opacity: 0.5 },
                            }}
                        >
                            <ChevronLeftIcon />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1a1a1a' }}>
                            {readAppLanguage() === 'ko' ? localize("{v0}년 {v1}월", { v0: year, v1: month }) : new Intl.DateTimeFormat(readAppLanguage() === 'ja' ? 'ja-JP' : 'en-US', { year: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 1)))}</Typography>
                        <Box
                            component="button"
                            aria-label={localize("다음 달")}
                            onClick={nextMonth}
                            sx={{
                                p: '6px', minWidth: 44, minHeight: 44, border: 0, bgcolor: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                                color: '#777',
                                '&:active': { opacity: 0.5 },
                            }}
                        >
                            <ChevronRightIcon />
                        </Box>
                    </Box>

                    {sharingPaused ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <SingleLineText sx={{ fontWeight: 700 }}>{localize("복용 기록 공유가 중단됐어요")}</SingleLineText>
                            <Typography sx={{ fontSize: 13, color: '#777', mt: 1 }}>{localize("가족이 앱에 다시 연결하면 확인할 수 있어요")}</Typography>
                        </Box>
                    ) : error ? (
                        <Box role="alert" sx={{ p: 3, textAlign: 'center' }}>
                            <SingleLineText sx={{ fontSize: 13 }}>{error}</SingleLineText>
                            <Button onClick={loadCalendar}>{localize("다시 불러오기")}</Button>
                        </Box>
                    ) : loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress size={28} sx={{ color: '#8a7000' }} />
                        </Box>
                    ) : (
                        <CalendarGrid
                            year={year}
                            month={month}
                            todayDate={today.getDate()}
                            selectedDate={selectedDate}
                            cellMedsByDay={cellMedsByDay}
                            onSelectDate={setSelectedDate}
                        />
                    )}
                </Box>

            </Box>

            <BottomSheet open={!!selectedDate} onClose={() => setSelectedDate(null)}>
                {selectedDate && (
                    <Box>
                    {showOwner && <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1, overflowWrap: 'anywhere' }}>{readOnly ? localize("{v0}의", { v0: ownerName }) : localize("내")}{localize(" 복용 기록")}{readOnly ? localize(" · 보기 전용") : ''}</Typography>}
                    {loading ? <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} aria-label={localize("복용 기록 불러오는 중")} /></Box> : <DayDetailCard
                        dateStr={selectedDate}
                        logs={selectedLogs}
                        showSkipReasons={!readOnly}
                        dayInfo={selectedDayInfo}
                        deletingIds={deletingIds}
                        onDeleteLog={readOnly ? undefined : setDeleteTarget}
                        processingIds={processingIds}
                        onMarkMissedTaken={readOnly ? undefined : openMissedConfirmSheet}
                        onEditTakenTime={readOnly ? undefined : openEditTimeSheet}
                    />}
                    </Box>
                )}
            </BottomSheet>

            <EditTakenTimeSheet
                open={timeSheet.open}
                mode="single"
                scheduledTime={timeSheet.log?.scheduledTime?.slice(0, 5) || timeSheet.initialTime}
                targets={timeSheet.log ? [{
                    id: String(timeSheet.log.id),
                    name: timeSheet.log.medicationName,
                    takenAt: timeSheet.initialTime,
                }] : []}
                initialTime={timeSheet.initialTime}
                scheduledDate={timeSheet.log?.scheduledDate}
                initialDate={timeSheet.mode === 'edit' ? timeSheet.log?.confirmedAt?.slice(0, 10) : timeSheet.log?.scheduledDate}
                action={timeSheet.mode === 'confirm' ? 'record' : 'edit'}
                loadConfirmationWindow={getConfirmationWindow}
                onClose={() => setTimeSheet((prev) => ({ ...prev, open: false }))}
                onConfirm={handleTimeSheetConfirm}
            />

            <Dialog
                dismissDisabled={deletingIds.has(deleteTarget?.id)}
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}
            >
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', px: 2.5, pt: 2.5, pb: 1 }}>{localize("복용 기록을 삭제할까요?")}</DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 0 }}>
                    <DialogContentText sx={{ fontSize: '1rem', color: '#666', lineHeight: 1.55 }}>{localize("삭제하면 복용 통계와 캘린더에서 제외돼요.")}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pt: 2.5, pb: 2.5, gap: 1 }}>
                    <Button
                        disabled={deletingIds.has(deleteTarget?.id)}
                        onClick={() => setDeleteTarget(null)}
                        variant="text"
                        sx={{
                            color: '#666', fontWeight: 600, fontSize: '1rem', borderRadius: 2,
                            flex: 1, py: 1.1, bgcolor: '#f5f5f5', whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: '#ececec' },
                        }}
                    >{localize("취소")}</Button>
                    <Button
                        disabled={deletingIds.has(deleteTarget?.id)}
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{
                            bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '1rem',
                            borderRadius: 2, flex: 1, py: 1.1, whiteSpace: 'nowrap', boxShadow: 'none',
                            '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' },
                        }}
                    >{localize("삭제")}</Button>
                </DialogActions>
            </Dialog>

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 4000 : 2500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
        </Box>
    );
}
